// Audit log — R-94: append-only, immutable, exportable. Admin+ only.

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listAuditLog, type AuditEntry } from '../lib/api';

export function AuditLogPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (nextCursor?: string) => {
    if (!user) return;
    const result = await listAuditLog(user.orgId, nextCursor);
    setEntries((prev) => nextCursor ? [...prev, ...result.entries] : result.entries);
    setCursor(result.nextCursor);
  }, [user]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  if (!user) return null;

  async function handleLoadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    await load(cursor).catch(() => undefined);
    setLoadingMore(false);
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-subtitle">
            Immutable action history for {user.orgName}. Append-only — no entry is ever
            modified or deleted (R-94).
          </p>
        </div>
        <button className="btn btn--secondary">Export CSV</button>
      </div>

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}

      {!loading && entries.length === 0 && (
        <div className="empty-state">
          <p className="empty-state__title">No audit events yet</p>
          <p className="empty-state__body">
            Admin actions — approvals, role changes, setting updates — are
            recorded here as they happen.
          </p>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Action</th>
                  <th scope="col">Target</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-muted)', fontSize: '0.8125rem', fontFamily: 'var(--font-mono)' }}>
                      {new Date(entry.createdAt).toLocaleString()}
                    </td>
                    <td style={{ fontWeight: 500 }}>{entry.actorName}</td>
                    <td>
                      <code style={{ fontSize: '0.8125rem', fontFamily: 'var(--font-mono)', background: 'var(--color-surface-alt)', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>
                        {entry.action}
                      </code>
                    </td>
                    <td style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                      {entry.targetType ?? '—'}{entry.targetId ? <> · <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{entry.targetId.slice(0, 8)}…</code></> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {cursor && (
            <div style={{ marginTop: 'var(--space-5)', textAlign: 'center' }}>
              <button
                className="btn btn--secondary"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
